import { AppBar, Box, Container, IconButton, makeStyles, Tab, Tabs, Toolbar, withWidth, WithWidthProps } from '@material-ui/core';
import { CloudDownload } from '@material-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { IFileResponse } from '../../models';
import * as XLSX from 'xlsx';
import { SharedService } from '../../services/shared.service';
import { useApp } from '../../providers/app.provider';
interface ExcelPreviewProps {
	file: IFileResponse;
}

const useStyles = makeStyles(theme => ({
	root: {
		'& table': {
			borderCollapse: 'collapse',
			borderSpacing: 0,
			width: '100%',
			border: '1px solid #ddd'
		},
		'& th, td': {
			textAlign: 'left',
			padding: '8px',
		},
		'& td': {
			border: '1px solid #000',
		},
		'& tr td:empty': {
			background: 'transparent',
			border: 'none',
			padding: '10px',
		},
		'& td:not(:empty) ~ td:empty': {
			border: '1px solid #000',
			background: 'inherit',
		},
		marginTop: theme.spacing(2),
		menuButton: {
			marginRight: theme.spacing(2),
		},
	},
	grow: {
		flexGrow: 1
	}
}));

interface TabPanelProps extends WithWidthProps {
	children?: React.ReactNode;
	index: number;
	value: string;
	currentIndex: number;
}

const TabPanel = withWidth()((props: TabPanelProps) => {
	const { children, value, index, currentIndex, ...other } = props;
	const { isSidebarOpen } = useApp();
	
	useEffect(() => {
		const htmlElement = document.getElementById(`scrollable-auto-tab-${index}`);
		if (htmlElement && value) {
			htmlElement.innerHTML = value;
		}
	}, [value, index]);

	const getSmallerWidth = () => {
		switch(props.width) {
			case 'xl': return 'lg';
			case 'lg': return 'md';
			case 'md': return 'sm';
			case 'sm': return 'xs';
			default: return props.width;
		}
	}

	return (
		<Box pt={currentIndex === index ? 1 : 0}>
			<Container maxWidth={isSidebarOpen ? getSmallerWidth() : props.width} disableGutters={true}>
				<div
					role='tabpanel'
					hidden={currentIndex !== index}
					id={`scrollable-auto-tabpanel-${index}`}
					aria-labelledby={`scrollable-auto-tab-${index}`}
					style={{ height: '50vh', overflow: 'auto', width: '100%' }}
					{...other}
				>
				</div>
			</Container>
		</Box>
	);
});

function a11yProps(index: any) {
	return {
		id: `scrollable-auto-tab-${index}`,
		'aria-controls': `scrollable-auto-tabpanel-${index}`,
	};
}

const ExcelPreviewComponent: React.FC<ExcelPreviewProps> = props => {
	const classes = useStyles();
	const [workbook, setWorkbook] = useState<XLSX.WorkBook>();
	const [currentSheet, setCurrentSheet] = useState(0);

	useEffect(() => {
		if (!props?.file) {
			return;
		}

		props.file.fileContents.arrayBuffer().then(x => {
			const data = new Uint8Array(x);
			const wb = XLSX.read(data, { type: 'array' });
			setWorkbook(wb);
			setCurrentSheet(0);
		});
	}, [props.file]);

	const downloadExcelFile = useCallback(() => {
		const sharedService: SharedService = new SharedService();
		sharedService.downloadFile(props.file);
	}, [props.file]);

	const handleSheetChange = (event: React.ChangeEvent<{}>, worksheetNumber: number) => {
		setCurrentSheet(worksheetNumber);
	};

	return (
		<div className={classes.root}>
			{props.file && (
				<>
					<AppBar position='static' color='transparent'>
						<Toolbar>
							<Tabs value={currentSheet} onChange={handleSheetChange}>
								{workbook?.SheetNames.map(sheetName => (
									<Tab key={`${sheetName}-tab`} label={sheetName} />
								))}
							</Tabs>
							<div className={classes.grow} />
							<IconButton
								aria-label="download button"
								aria-controls="download-appbar"
								aria-haspopup="true"
								onClick={downloadExcelFile}
								color="inherit"
							>
								<CloudDownload />
							</IconButton>
						</Toolbar>
					</AppBar>
					{workbook?.SheetNames.map((sheetName, index) => {
						const worksheet = workbook?.Sheets[sheetName];
						const htmlText = XLSX.utils.sheet_to_html(worksheet!);
						return (
							<TabPanel
								key={`${sheetName}-panel`}
								index={index}
								currentIndex={currentSheet}
								value={htmlText}
								{...a11yProps(index)}
							/>
						);
					})}
				</>
			)}
		</div>
	);
};

export default ExcelPreviewComponent;
